import { useParams } from 'react-router-dom'
import { listVaccinations } from '../../api/clinical'
import { getPatient } from '../../api/patients'
import { PrintShell } from '../../components/PrintShell'
import { formatPrintDate, printLabel, printTd, printTh } from '../../lib/print'
import { useFetch } from '../../hooks/useFetch'
import { SEX_LABELS, SPECIES_LABELS } from '../../types/patients'

export default function VaccinationCertificate() {
  const { id } = useParams<{ id: string }>()
  const { data, loading, error } = useFetch(
    () => Promise.all([getPatient(Number(id)), listVaccinations(Number(id))]),
    [id],
  )

  return (
    <PrintShell title={data ? `Vaccination certificate — ${data[0].name}` : 'Vaccination certificate'} loading={loading} error={error}>
      {() => {
        const [patient, vaccinations] = data!
        const owner = patient.owners.find(o => o.is_primary) ?? patient.owners[0]
        // Issued by the practice that gave the most recent vaccination.
        const issuer = vaccinations[0]?.practice_name
        const rows: [string, string | null | undefined][] = [
          ['Name', patient.name],
          ['Species', SPECIES_LABELS[patient.species]],
          ['Breed', patient.breed],
          ['Sex', SEX_LABELS[patient.sex]],
          ['Date of birth', patient.date_of_birth && formatPrintDate(patient.date_of_birth)],
          ['Colour', patient.color],
          ['Microchip', patient.microchip_number],
          ['Owner', owner && `${owner.first_name} ${owner.last_name}`],
        ]
        return (
          <>
            <div style={{ borderBottom: '2px solid #111', paddingBottom: '16px', marginBottom: '24px' }}>
              <h1 style={{ margin: 0, fontSize: '26px' }}>Vaccination Certificate</h1>
              {issuer && <div style={{ color: '#444', marginTop: '4px' }}>{issuer}</div>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: '28px' }}>
              {rows.filter(([, v]) => v).map(([label, value]) => (
                <div key={label}><div style={printLabel}>{label}</div><div>{value}</div></div>
              ))}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #111' }}>
                  <th style={printTh}>Vaccine</th>
                  <th style={printTh}>Date given</th>
                  <th style={printTh}>Batch</th>
                  <th style={printTh}>Next due</th>
                </tr>
              </thead>
              <tbody>
                {vaccinations.length === 0 ? (
                  <tr><td colSpan={4} style={{ ...printTd, color: '#888', fontStyle: 'italic' }}>No vaccinations recorded</td></tr>
                ) : vaccinations.map(v => (
                  <tr key={v.id}>
                    <td style={printTd}>{v.vaccine_name}</td>
                    <td style={printTd}>{formatPrintDate(v.date_given)}</td>
                    <td style={printTd}>{v.batch_number ?? '—'}</td>
                    <td style={printTd}>{v.next_due_date ? formatPrintDate(v.next_due_date) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )
      }}
    </PrintShell>
  )
}
